import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
  CommonTag,
  ExportOptions,
  ExportResponse,
  FilterGroup,
  LinkedDataTransaction,
  LinkedDataTransactionUpdate,
  PaginatedFetch,
  PaginatedMeta,
  Sort,
} from '@types'

import { PAGE_SIZE } from '@/const/common'

import { prepareResponseError, updateStoreListItem } from '../utils/helpers'
import { prepareExportParams } from '@/helpers/common'

import { useRepositoriesStore } from '../repositories/index'
import { useLinkedDataTransactionsPivotStore } from './transactionsPivot'

import api, { getPaginatedRequest } from '@/store/api'

export const useLinkedDataTransactionsStore = defineStore(
  'linked-data-transactions',
  () => {
    // INIT
    const repositoriesStore = useRepositoriesStore()
    const linkedDataTransactionsPivotStore =
      useLinkedDataTransactionsPivotStore()

    const list = ref<LinkedDataTransaction[]>([])

    const initFlag = ref(false)

    const loading = ref(true)
    const loadingAction = ref(false)

    const meta = ref<PaginatedMeta>()

    let abortController = new AbortController()

    const repositoryId = computed(() => repositoriesStore.getCurrentId)

    const search = ref<string>()
    const filter = ref<FilterGroup | null>()
    const sort = ref<Sort[] | null>()

    // GETTERS
    const getList = computed(() => list.value)

    const getMeta = computed(() => meta.value)

    const getSearch = computed(() => search.value)
    const getFilter = computed(() => filter.value)
    const getSort = computed(() => sort.value)

    // SETTERS
    const setList = (
      data: LinkedDataTransaction[],
      metaData?: PaginatedMeta,
    ) => {
      list.value = data
      meta.value = metaData
    }

    const setOptions = (options: {
      search?: string
      filter?: FilterGroup | null
      sort?: Sort[] | null
    }) => {
      search.value = options.search || ''
      filter.value = options.filter || null
      sort.value = options.sort || null
    }

    // ACTIONS

    const fetch: PaginatedFetch = async (
      options,
      store = true,
      signal?: AbortSignal,
      useStoreOptions = true,
    ) => {
      if (store) loading.value = true
      try {
        const { data, meta } = await getPaginatedRequest<LinkedDataTransaction>(
          'linked_data/transactions',
          {
            repository_id: repositoryId.value,
            page_size: PAGE_SIZE,
            ...(useStoreOptions ? {
              search: getSearch.value,
              filter: getFilter.value,
              sort: getSort.value,
            } : {}),
            ...options,
          },
          signal,
        )
        if (store) {
          setList(data, meta)
          initFlag.value = true
        } else {
          return { data, meta }
        }
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        if (store) loading.value = false
      }
    }

    const exportList = async (options: ExportOptions) => {
      try {
        const params = prepareExportParams(options)
        const response = await api.get<ExportResponse>(
          'linked_data/transactions/export',
          {
            params: { repository_id: repositoryId.value, ...params }
          },
        )
        const url = response.data?.file_url
        if (!url) throw Error('Linked Data Transactions export URL is not available')
        const json = await api.get(url)
        return json.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const bulkUpdate = async (
      data: LinkedDataTransactionUpdate[],
      store = true,
    ) => {
      if (store) loadingAction.value = true
      try {
        const result = await api.put<LinkedDataTransaction[]>(
          'linked_data/transactions',
          {
            data: data,
            repository_id: repositoryId.value,
          },
        )
        result.data.forEach(item => {
          updateStoreListItem(list.value, item)
        })
        linkedDataTransactionsPivotStore.clear()
        return result.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        if (store) loadingAction.value = false
      }
    }

    const getJSON = async (id: string): Promise<any> => {
      try {
        const result = await api.get(`linked_data/transactions/${id}`, {
          params: { repository_id: repositoryId.value },
          signal: abortController.signal,
        })
        return result.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const removeTag = (id: string, tagId: string) => {
      const index = list.value.findIndex(item => item.id === id)
      list.value[index].tags = list.value[index].tags.filter(
        tag => tag.id !== tagId,
      )
    }

    const assignTags = (id: string, tags: CommonTag[]) => {
      const index = list.value.findIndex(item => item.id === id)
      list.value[index].tags = tags
    }

    const cancel = () => {
      abortController.abort()
      abortController = new AbortController()
    }

    const clear = () => {
      initFlag.value = false

      loading.value = true

      setList([], undefined)
    }

    return {
      initFlag,
      loading,
      loadingAction,

      setOptions,

      getList,
      getMeta,

      fetch,
      export: exportList,
      bulkUpdate,

      getJSON,

      removeTag,
      assignTags,

      cancel,
      clear,
    }
  },
)
